import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  MoreHorizontal,
  Plus,
  RefreshCw,
} from "lucide-react";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import { useAuthStore } from "../../../stores/auth-store";
import { bankTransferAutoConfirmApiService } from "../../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";
import type {
  AutoConfirmOverview,
  AutoConfirmSubscription,
} from "../../../../domain/types/bank-transfer-auto-confirm";
import { AccountTab } from "./AccountTab";
import { AccountEditModal } from "./AccountEditModal";
import { DepositTab } from "./DepositTab";
import { ManualAccountModal } from "./ManualAccountModal";
import { PaymentMethodModal } from "./PaymentMethodModal";
import { PurchaseWizard } from "./PurchaseWizard";
import { MAX_AUTO_CONFIRM_ACCOUNTS } from "./shared";
import { companyBankTransferUseCase } from "../../../../di/container";
import type { BankTransferEntity } from "../../../../domain/types/company-bank-transfer";
import { money } from "../bankTransferFormat";

type Tab = "deposit" | "account";

const LIVE = ["ACTIVE", "ACTIVATING", "PAST_DUE", "CANCEL_REQUESTED"];

/**
 * 결제 관리 › 무통장 입금.
 *
 * **입금 확인과 계좌 관리가 한 화면이다.** 예전에는 주문 관리에 따로 있었는데,
 * 같은 기능을 두 곳에서 찾게 만들 이유가 없다.
 *
 * 화면을 여는 기준은 **계좌**지 자동확인 신청이 아니다. 신청으로 잠그면, 계좌를 등록해 둔
 * 회사가 자기 계좌를 보지도 끄지도 못한다 — 고객 결제창에는 그대로 보이는데도.
 * 무통장입금 자체는 요금이 없고, 통장을 대신 봐 주는 자동확인부터가 유료다.
 *
 * 탭 순서가 곧 쓰는 빈도다 — 입금 확인은 매일, 계좌 관리는 가끔이다.
 * 결제수단은 ⋯ 안으로 넣었다. 돈 이야기가 늘 눈앞에 있을 필요는 없다.
 * 다만 **결제가 깨졌을 때는 배너로 끌어올린다** — 방치하면 계좌가 끊긴다.
 */
export const BankTransfer: React.FC = () => {
  const { companyId: companyIdParam } = useParams();
  const companyId = Number(companyIdParam);
  const role = useAuthStore((s) => s.user?.role);

  /** 돈이 오가는 일은 매니저에게 열지 않는다. 서버도 같은 기준으로 막는다 */
  const readOnly = role === "COMPANY_MANAGER";

  const [tab, setTab] = useState<Tab>("deposit");
  const [overview, setOverview] = useState<AutoConfirmOverview>();
  const [accounts, setAccounts] = useState<BankTransferEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<AutoConfirmSubscription>();
  /** 수기 계좌 창. 새로 등록이면 true, 고칠 계좌면 그 계좌 */
  const [manualOpen, setManualOpen] = useState<BankTransferEntity | true>();

  const reload = useCallback(async () => {
    if (!Number.isFinite(companyId)) return;
    setLoading(true);
    setError("");
    try {
      const [nextOverview, nextAccounts] = await Promise.all([
        bankTransferAutoConfirmApiService.overview(companyId),
        companyBankTransferUseCase.getBankTransfer(companyId),
      ]);
      setOverview(nextOverview);
      setAccounts(nextAccounts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * 자동확인 신청. 결제까지 가려면 서버에 신청 이력이 있어야 해서 마법사보다 먼저 부른다.
   * 신청만으로는 요금이 생기지 않는다 — 계좌를 살 때부터다.
   */
  const startAuto = async () => {
    if (readOnly || applying) return;
    if (overview?.applied) {
      setWizardOpen(true);
      return;
    }
    setApplying(true);
    setError("");
    try {
      setOverview(await bankTransferAutoConfirmApiService.apply(companyId));
      setTab("account");
      setWizardOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "신청하지 못했습니다.");
    } finally {
      setApplying(false);
    }
  };

  const live = useMemo(
    () =>
      overview?.subscriptions.filter((it) => LIVE.includes(it.status)) ?? [],
    [overview],
  );
  const pastDue = live.filter((it) => it.status === "PAST_DUE");
  /** 자동확인을 이용 중인 회사인지. 이때는 수기 등록 경로를 열지 않는다 */
  const autoMode = live.length > 0;
  /** 계좌 상한. 서버도 같은 수로 막으므로 화면에서 미리 잠근다 */
  const full = accounts.length >= MAX_AUTO_CONFIRM_ACCOUNTS;
  /** 계좌가 하나라도 있으면 화면을 연다. 신청 이력은 유료 구간의 조건일 뿐이다 */
  const hasScreen = accounts.length > 0 || autoMode;
  const next = live
    .filter((it) => it.nextBillingAt)
    .sort((a, b) => (a.nextBillingAt! < b.nextBillingAt! ? -1 : 1))[0];
  const nextTotal = live
    .filter(
      (it) => it.nextBillingAt && it.nextBillingAt === next?.nextBillingAt,
    )
    .reduce((sum, it) => sum + it.amount, 0);

  return (
    <CompanyAdminLayout>
      <div className="w-full min-w-0 max-w-full overflow-x-hidden p-4 text-left sm:p-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[22px] font-bold text-[#222222]">
              무통장 입금
            </h1>
            <p className="mt-1 text-sm text-[#888888]">
              {!hasScreen
                ? "계좌를 등록하면 고객이 무통장으로 결제할 수 있습니다."
                : autoMode
                  ? "들어온 입금을 주문과 맞춰 확인하고, 계좌와 이용 상태를 관리합니다."
                  : "등록한 계좌를 관리하고, 받은 입금을 주문에 반영합니다."}
            </p>
          </div>

          {hasScreen && (
            <div className="relative flex items-center gap-2">
              {/* 결제수단은 자동확인을 쓸 때만 있는 것이다. 수기 계좌만 쓰는 회사에는 낼 돈이 없다 */}
              {overview?.applied && (
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  disabled={readOnly}
                  aria-label="더보기"
                  className="grid h-[38px] w-[38px] place-items-center rounded-lg border border-[#E0E0E0] bg-white text-[#555555] hover:bg-[#F5F5F5] disabled:text-[#BBBBBB]"
                >
                  <MoreHorizontal size={18} />
                </button>
              )}
              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-[44px] z-20 w-[180px] overflow-hidden rounded-[8px] border border-[#E0E0E0] bg-white shadow-lg">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setPayOpen(true);
                      }}
                      className="block w-full px-4 py-3 text-left text-[13px] text-[#555555] hover:bg-[#F5F5F5]"
                    >
                      결제수단 관리
                    </button>
                  </div>
                </>
              )}
              <button
                onClick={() =>
                  autoMode ? setWizardOpen(true) : setManualOpen(true)
                }
                disabled={readOnly || full}
                title={
                  full
                    ? `계좌는 최대 ${MAX_AUTO_CONFIRM_ACCOUNTS}개까지 등록할 수 있습니다.`
                    : undefined
                }
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#4285F4] px-4 py-2.5 text-[13px] font-bold text-white hover:bg-[#3367D6] disabled:cursor-not-allowed disabled:bg-[#BBBBBB]"
              >
                <Plus size={16} /> 계좌 추가하기
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-[12px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3 text-sm text-[#4285F4]">
            <AlertCircle size={17} /> {error}
          </div>
        )}

        {/*
          평소에는 결제수단을 ⋯ 안에 숨기지만, 결제가 깨지면 오히려 눈에 띄어야 한다.
          사장님이 손대야 풀리는 문제이고 방치하면 유예가 끝나 계좌가 끊긴다.
        */}
        {pastDue.length > 0 && (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3">
            <div className="text-[13px] text-[#4285F4]">
              결제가 확인되지 않아 재시도 중입니다. 결제수단을 확인해주세요.
            </div>
            <button
              onClick={() => setPayOpen(true)}
              disabled={readOnly}
              className="rounded-lg border border-[#4285F4]/30 bg-white px-3 py-2 text-[12px] font-medium text-[#4285F4] hover:bg-[#EBF3FF] disabled:text-[#BBBBBB]"
            >
              결제수단 확인
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex min-h-[440px] items-center justify-center rounded-[12px] border border-[#E0E0E0] bg-white text-[#888888]">
            <RefreshCw className="mr-2 animate-spin" size={18} /> 정보를
            불러오는 중입니다
          </div>
        ) : !hasScreen ? (
          <StartHero
            onManual={() => setManualOpen(true)}
            onAuto={() => void startAuto()}
            busy={applying}
            disabled={readOnly}
          />
        ) : (
          <>
            <div className="mb-5 flex gap-1 border-b border-[#EEEEEE]">
              <TabButton
                on={tab === "deposit"}
                onClick={() => setTab("deposit")}
              >
                입금 확인
              </TabButton>
              <TabButton
                on={tab === "account"}
                onClick={() => setTab("account")}
              >
                계좌 관리
                <span className="ml-1.5 rounded-full bg-[#F5F5F5] px-1.5 py-0.5 text-[11px] font-bold text-[#555555]">
                  {accounts.length}
                </span>
              </TabButton>
            </div>

            {tab === "deposit" ? (
              <DepositTab
                companyId={companyId}
                onStartAuto={() => void startAuto()}
              />
            ) : (
              <AccountTab
                companyId={companyId}
                accounts={accounts}
                subscriptions={overview?.subscriptions ?? []}
                readOnly={readOnly}
                autoMode={autoMode}
                onAddAuto={() => void startAuto()}
                onAddManual={() => setManualOpen(true)}
                onEditAuto={setEditing}
                onEditManual={setManualOpen}
                onChanged={reload}
              />
            )}
          </>
        )}
      </div>

      {payOpen && overview && (
        <PaymentMethodModal
          companyId={companyId}
          overview={overview}
          nextBillingAt={next?.nextBillingAt}
          nextBillingTotal={nextTotal}
          readOnly={readOnly}
          onClose={() => setPayOpen(false)}
          onChanged={reload}
        />
      )}

      {wizardOpen && (
        <PurchaseWizard
          companyId={companyId}
          hasBillingMethod={overview?.billingMethod?.status === "ACTIVE"}
          onClose={() => setWizardOpen(false)}
          onCompleted={async () => {
            setWizardOpen(false);
            await reload();
          }}
        />
      )}

      {manualOpen && (
        <ManualAccountModal
          companyId={companyId}
          account={manualOpen === true ? undefined : manualOpen}
          onClose={() => setManualOpen(undefined)}
          onSaved={async () => {
            setManualOpen(undefined);
            await reload();
          }}
        />
      )}

      {editing && (
        <AccountEditModal
          companyId={companyId}
          item={editing}
          onClose={() => setEditing(undefined)}
          onDone={async () => {
            setEditing(undefined);
            await reload();
          }}
        />
      )}
    </CompanyAdminLayout>
  );
};

const TabButton = ({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`relative px-[18px] pb-3 pt-2.5 text-sm font-semibold transition ${
      on ? "text-[#4285F4]" : "text-[#888888] hover:text-[#555555]"
    }`}
  >
    {children}
    {on && (
      <span className="absolute inset-x-2 -bottom-px h-0.5 rounded bg-[#4285F4]" />
    )}
  </button>
);

/**
 * 계좌가 하나도 없을 때의 시작 화면.
 *
 * **두 갈래를 나란히 보여준다.** 무통장입금 자체는 요금이 없다 — 계좌를 등록하면 바로 쓴다.
 * 돈을 내는 것은 통장을 대신 봐 주는 자동확인이고, 그 차이가 카드 두 장으로 드러나야
 * 사장님이 무엇을 사는지 알고 고른다.
 */
const StartHero = ({
  onManual,
  onAuto,
  busy,
  disabled,
}: {
  onManual: () => void;
  onAuto: () => void;
  busy: boolean;
  disabled: boolean;
}) => (
  <div className="rounded-[12px] border border-[#E0E0E0] bg-white px-5 py-12 text-center sm:px-8 sm:py-14">
    <h2 className="text-[24px] font-extrabold tracking-[-0.5px] text-[#222222] sm:text-[28px]">
      아직 등록된 무통장 계좌가 없어요
    </h2>
    <p className="mx-auto mt-3 max-w-[520px] text-[14px] leading-7 text-[#888888] sm:text-[15px]">
      계좌를 등록하면 고객이 무통장으로 결제할 수 있습니다.
      <br />
      입금 확인은 직접 하셔도 되고, 맡기셔도 됩니다.
    </p>

    <div className="mx-auto mt-9 grid max-w-[820px] gap-5 text-left md:grid-cols-2">
      <Way
        title="계좌만 등록하기"
        price="무료"
        items={[
          "쇼핑몰 결제창에 계좌가 표시됩니다",
          "입금은 은행 앱에서 직접 확인합니다",
          "주문은 수동으로 관리합니다",
        ]}
        cta="계좌 등록하기"
        onClick={onManual}
        disabled={disabled}
      />
      <Way
        recommended
        title="자동확인까지 시작하기"
        price={`계좌당 월 ${money(10000)}`}
        items={[
          "입금 내역을 자동으로 확인합니다",
          "입금자명과 금액이 맞으면 자동으로 접수됩니다",
          "확실하지 않은 입금은 손대지 않고 알려 드립니다",
        ]}
        cta={busy ? "신청 중입니다…" : "자동확인 시작하기 →"}
        onClick={onAuto}
        disabled={disabled || busy}
      />
    </div>

    {disabled && (
      <p className="mt-2 text-[12px] text-[#888888]">
        계좌 등록과 신청은 회사 대표 계정에서만 할 수 있습니다.
      </p>
    )}
  </div>
);

const Way = ({
  title,
  price,
  items,
  cta,
  onClick,
  disabled,
  recommended = false,
}: {
  title: string;
  price: string;
  items: string[];
  cta: string;
  onClick: () => void;
  disabled: boolean;
  recommended?: boolean;
}) => (
  <div
    className={`relative flex min-h-[260px] flex-col rounded-[12px] border bg-white p-6 sm:p-7 ${
      recommended
        ? "border-[#4285F4] ring-[3px] ring-[#4285F4]/15"
        : "border-[#E0E0E0]"
    }`}
  >
    {recommended && (
      <span className="absolute -top-2.5 right-5 rounded bg-[#4285F4] px-2.5 py-1 text-[11px] font-bold text-white">
        추천
      </span>
    )}
    <div className="text-[17px] font-bold text-[#222222]">{title}</div>
    <div
      className={`mt-1.5 text-[14px] font-semibold ${
        recommended ? "text-[#4285F4]" : "text-[#888888]"
      }`}
    >
      {price}
    </div>
    <ul className="mb-6 mt-4 list-disc pl-[18px]">
      {items.map((item) => (
        <li key={item} className="text-[13px] leading-[25px] text-[#555555]">
          {item}
        </li>
      ))}
    </ul>
    <button
      onClick={onClick}
      disabled={disabled}
      className={`mt-auto min-h-[44px] w-full rounded-lg px-4 py-2.5 text-[14px] font-bold disabled:cursor-not-allowed ${
        recommended
          ? "bg-[#4285F4] text-white hover:bg-[#3367D6] disabled:bg-[#BBBBBB]"
          : "border border-[#222222] text-[#222222] hover:bg-[#F5F5F5] disabled:border-[#E0E0E0] disabled:text-[#BBBBBB]"
      }`}
    >
      {cta}
    </button>
  </div>
);

export default BankTransfer;
